import React, { useLayoutEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
	extractTextContent,
	nullSafeGet,
	nullSafeGetOrElse,
	getWorkOrderTimeZoneId,
	stopDefaultEvents,
	isSupplierPayingCustomer,
} from '../../utils/DataAccessUtils';
import { isInternalTech } from '../../utils/AuthUtils';
import { getAssetDisplayName } from '../../utils/assetUtils';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import WorkOrderDisplayStatusDisplay, {
	getStatusDisplayName,
} from '../work_order_display_status_display/WorkOrderDisplayStatusDisplay';
import {
	getWorkOrderBasePriorityBackgroundColor,
	getWorkOrderBasePriorityPillBackgroundColor,
	isDatePastDue,
} from '../../utils/DataFormatterUtils';
import warrantyDisplay from '../common/WarrantyDisplay';
import { WORK_ORDER_STATUSES } from '../../constants/WorkOrderStatuses';
import DateTimeHover from '../date_time_component/DateTime';
import { WORK_ORDER_DISPLAY_STATUSES } from '../../constants/WorkOrderDisplayStatuses';
import PrivateWorkOrderLock from '../private_work_order_lock/PrivateWorkOrderLock';
import { connect } from 'react-redux';
import {
	getBuyerOrContractorNameWithHighlightKey,
	getSanitisedWorkOrder,
	getWorkOrderLink,
} from '../../utils/WorkOrderUtils';
import { openInternalWorkspace } from '../../actions/internal_workspace_actions';
import { Popover } from 'antd';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import WorkOrderTechAssignedPill from '../work_order_tech_assigned_pill/WorkOrderTechAssignedPill';
import WorkOrderRowNotePreview from './work_order_row_note_preview/WorkOrderRowNotePreview';
import GridColumnSeparator from '../row_note_preview/GridColumnSeparator';
import { restoreCasingInHighlight } from '../../utils/SearchHighlightUtils';
import WorkOrderStarIcon from '../StarLabels/WorkOrderStarIcon';
import WorkOrderLabelChipsRow from '../StarLabels/WorkOrderLabelChipsRow';
import StarNotePopover from '../StarLabels/StarNotePopover';
import AddLabelModal from '../StarLabels/WorkOrderAddLabelModal';
import {
	getWorkOrderLabels,
	getWorkOrderStarNote,
	isWorkOrderStarred,
	isWorkOrderLabelsTrackEnabled,
} from '../../data/workOrderStarLabels';
const moment = require('moment');
require('./WorkOrderRowDisplay.less');

/**
 * Defensive normalization of the search-response highlight bag.
 *
 * The backend returns Elasticsearch highlight keys with `.prefixed` / `.stemmed`
 * analyzer suffixes (e.g. `notes.prefixed`, `description.stemmed`) and surfaces
 * internal-note hits under `internalNotes`. The list adaptor (workOrderSearchAndAPIAdaptor)
 * normalizes those into base keys (`notes`, `description`) and aliases
 * `internalNotes` → `internalSpaces` — but the row component is also used in
 * code paths that don't go through that adaptor (linked work orders, embedded
 * pickers, etc.), and in those cases the raw suffixed keys land here verbatim
 * and the renderer's exact-key lookups (`displayedFields[key]`, `highlightLabels[key]`,
 * `highlight.notes`, `highlight.internalSpaces`) silently miss every hit.
 *
 * Normalize once at component entry so the rest of the file can keep using
 * canonical base keys with no special-casing per call site.
 */
const normalizeHighlight = (rawHighlight: any): Record<string, string[]> | null => {
	if (!rawHighlight) return null;
	const normalized: Record<string, string[]> = {};
	// Track which analyzer source supplied each base key so a later iteration
	// order can't let `.stemmed` beat `.prefixed`. Precedence (highest first):
	// base (no suffix) > .prefixed > .stemmed. Matches workOrderSearchAndAPIAdaptor.
	const SOURCE_PRIORITY: Record<string, number> = { base: 3, prefixed: 2, stemmed: 1 };
	const sourceByBaseKey: Record<string, string> = {};
	const keys = Object.keys(rawHighlight);
	for (let i = 0; i < keys.length; i++) {
		const key = keys[i];
		const value = rawHighlight[key];
		if (!value) continue;
		const baseKey = key.replace(/(\.stemmed|\.prefixed)$/, '');
		const source = key === baseKey ? 'base' : key.endsWith('.prefixed') ? 'prefixed' : 'stemmed';
		const existingSource = sourceByBaseKey[baseKey];
		if (!existingSource || SOURCE_PRIORITY[source] > SOURCE_PRIORITY[existingSource]) {
			normalized[baseKey] = value;
			sourceByBaseKey[baseKey] = source;
		}
	}
	if (rawHighlight.troubleshooting) {
		(normalized as any).troubleshooting = rawHighlight.troubleshooting;
	}
	// Backend names internal-note highlights `internalNotes`; the rest of the
	// UI reads them under `internalSpaces`.
	if (normalized.internalNotes && !normalized.internalSpaces) {
		normalized.internalSpaces = normalized.internalNotes;
		delete normalized.internalNotes;
	}
	return normalized;
};

// Apply the search query's casing to the highlighted HTML when the highlight
// covers the entire query token, so e.g. searching "OW-3683" against a doc
// stored as "ow-3683" renders as "OW-3683" (matching what the user typed)
// instead of the lowercase source. For partial matches (where the highlight
// does not equal the query case-insensitively), the original highlight HTML
// is returned unchanged.
// Read the active list-search query from the URL. ES highlights come back with
// the source's casing (often lowercase because the backend stores serial numbers
// etc. in lowercase), but users expect their typed query to be reflected back —
// e.g. searching "OW-3683" should render "OW-3683", not "ow-3683". We can't
// retrieve the query as a hook-derived prop here without rewiring every caller,
// so we read it directly from window.location at render time.
const getActiveSearchQuery = (): string | undefined => {
	if (typeof window === 'undefined') return undefined;
	try {
		const value = new URLSearchParams(window.location.search).get('search');
		const trimmed = value && value.trim();
		return trimmed ? trimmed : undefined;
	} catch {
		return undefined;
	}
};

// Apply the search query's casing to the highlighted HTML when the highlight
// covers the entire query token, so e.g. searching "OW-3683" against a doc
// stored as "ow-3683" renders as "OW-3683". For partial matches (where the
// highlight does not equal the query case-insensitively), the original
// highlight HTML is returned unchanged.
const applySearchCasing = (highlightStr: string): string => {
	const searchQuery = getActiveSearchQuery();
	if (!searchQuery) return highlightStr;
	return restoreCasingInHighlight(highlightStr, searchQuery);
};

const renderWithHighlight = (highlight, keys, defaultValue, shouldMatchDefaultValue = false) => {
	const highlightValue = highlight && highlight[keys];

	const isMatchingWithDefaultValue =
		highlightValue && highlightValue[0] && shouldMatchDefaultValue
			? defaultValue.includes(highlightValue[0].replace(/<\/?em>/g, ''))
			: true;

	if (highlightValue && isMatchingWithDefaultValue) {
		return (
			<span className="highlights">
				<span
					dangerouslySetInnerHTML={{
						__html: applySearchCasing(highlightValue[0]),
					}}
				/>
			</span>
		);
	}
	return defaultValue;
};

// these are the fields that are displayed in the work order row. so not needed to show again.
const displayedFields = {
	locationName: 'Location Name',
	assetName: 'Asset Name',
	assetNumber: 'Asset Number',
	woPriorityName: 'Priority',
	problemTypeName: 'Problem Type',
	supplierFacilityDisplayName: 'Supplier Facility',
	id: 'Work Order ID',
	poNumber: 'PO Number',
	dueDate: 'Due Date',
	createdAt: 'Created At',
	estimatedCompletionDate: 'Estimated Completion Date',
	title: 'Title',
	notes: 'Notes',
	internalSpaces: 'Internal Notes',
	problemTypeHierarchyName: 'Problem Type',
	buyerProjectTitles: 'Buyer Projects',
};

const highlightLabels = {
	description: 'Description',
	notes: 'Notes',
	internalSpaces: 'Internal Notes',
	serviceCallCheckOutNotes: 'Check Out Notes',
	serviceCallCheckInNotes: 'Check In Notes',
	poNumber: 'PO Number',
	buyerSpecificId: 'Buyer Specific ID',
	woNumber: 'Work Order Number ',
	assetTypeName: 'Asset Type',
	assetSerialNumber: 'Asset Serial Number',
	assetEquipmentUniqueId: 'Asset Equipment Unique ID',
	assetId: 'Asset ID',
	buyerSpecificAssetId: 'Buyer Specific Asset ID',
};

// Conversation-note highlight channels. These are suppressed for subcontracted
// supplier rows because the row's note content is not reliably scoped to the
// viewing supplier's channel (OPE-4842). Non-note highlights (description, asset
// fields, troubleshooting, etc.) are unaffected.
const NOTE_HIGHLIGHT_KEYS = ['notes', 'internalSpaces', 'contractorNotes'];

const WorkOrderRowDisplay: React.FC<any> = ({
	workOrder,
	simpler,
	currentUser,
	userType,
	isClientView,
	hideDueDate = false,
	titleActions = null,
	showFullTitle = false,
	targetCollectionName = null,
	refreshTable,
	hideNotePreview = false,
	hideSupplierName = false,
	openInternalWorkspace,
	labelsPending = false,
}) => {
	const history = useHistory();
	const location = useLocation();
	const [addLabelModalOpen, setAddLabelModalOpen] = useState(false);
	const rowPrimaryRef = useRef<HTMLDivElement | null>(null);
	const titleSlotRef = useRef<HTMLDivElement | null>(null);
	const titleMeasureRef = useRef<HTMLSpanElement | null>(null);
	const labelsInnerRef = useRef<HTMLDivElement | null>(null);
	const [moveStarToNewLine, setMoveStarToNewLine] = useState(false);
	const [labelsOnNewLine, setLabelsOnNewLine] = useState(false);

	const starred = isWorkOrderStarred(workOrder);
	const starNote = getWorkOrderStarNote(workOrder);
	const workOrderLabels = getWorkOrderLabels(workOrder);
	// Star + label surfaces (star pill, label chips, add-label modal) are hidden when the flag is off.
	const labelsTrackEnabled = isWorkOrderLabelsTrackEnabled();
	const workOrderId = workOrder && workOrder.id;
	const workOrderTitle = workOrder && workOrder.title;
	const labelsSignature = workOrderLabels.map((label) => label.id).join(',');

	useLayoutEffect(() => {
		if (!workOrder) return undefined;
		const condition = starred && !!starNote && workOrderLabels.length > 0;
		if (!condition) {
			setMoveStarToNewLine(false);
			return undefined;
		}
		const titleEl = titleMeasureRef.current;
		const slotEl = titleSlotRef.current;
		if (!titleEl || !slotEl) return undefined;
		const check = () => {
			if (titleEl.scrollWidth > slotEl.clientWidth + 1) {
				setMoveStarToNewLine(true);
			}
		};
		setMoveStarToNewLine(false);
		check();
		const observer = new ResizeObserver(check);
		observer.observe(slotEl);
		return () => observer.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workOrderId, workOrderTitle, starred, starNote, workOrderLabels.length]);

	useLayoutEffect(() => {
		if (!workOrder) return undefined;
		if (workOrderLabels.length === 0) {
			setLabelsOnNewLine(false);
			return undefined;
		}
		const containerEl = rowPrimaryRef.current;
		if (!containerEl) return undefined;
		const ROW_GAP_PX = 12; // matches gap-3 between the title and the labels
		const check = () => {
			// Read refs fresh each call: labelsInnerRef's node moves between the inline
			// and new-line branches as labelsOnNewLine flips, so a captured reference
			// would go stale after a re-parent and silently measure a detached node.
			const currentContainerEl = rowPrimaryRef.current;
			const titleSlotEl = titleSlotRef.current;
			const titleEl = titleMeasureRef.current;
			const labelsEl = labelsInnerRef.current;
			if (!currentContainerEl || !titleSlotEl || !titleEl || !labelsEl) return;
			const available = currentContainerEl.clientWidth - titleSlotEl.offsetLeft;
			const titleNaturalWidth = titleEl.scrollWidth;
			const labelsNaturalWidth = labelsEl.scrollWidth;
			setLabelsOnNewLine(titleNaturalWidth + labelsNaturalWidth + ROW_GAP_PX > available);
		};
		check();
		const observer = new ResizeObserver(check);
		observer.observe(containerEl);
		return () => observer.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workOrderId, workOrderTitle, labelsSignature]);

	if (!workOrder) {
		return null;
	}

	const hasEstimatedCompletionDate = !!workOrder.estimatedCompletionDate;
	const isDueDateSameAsCompletionDate = hasEstimatedCompletionDate
		? moment(workOrder.dueDate).diff(workOrder.estimatedCompletionDate, 'hours') === 0
		: true;
	const isPastDue = isDatePastDue(workOrder.dueDate);

	const isPastEstimatedCompletionDate =
		isPastDue && isDueDateSameAsCompletionDate
			? true
			: moment(workOrder.estimatedCompletionDate).diff(moment(), 'milliseconds') < 0;

	const isNotClosed =
		workOrder.displayStatus !== 'Completed' && workOrder.displayStatus !== 'Cancelled';
	const normalizedHighlight = normalizeHighlight(workOrder.highlight);
	const highlight =
		normalizedHighlight && Object.keys(normalizedHighlight).length > 0 ? normalizedHighlight : null;

	// Subcontracted work orders carry notes across multiple channels (buyer ↔ prime,
	// prime ↔ sub), and the row's "last note" is not reliably scoped to the viewing
	// channel — so a note that shouldn't be visible can leak. Until the note is properly
	// access-gated, suppress the last-note preview and note-highlight snippets for any
	// work order that has subcontracting relationships. (OPE-4842)
	const hideLastNotesForSubcontracted =
		nullSafeGetOrElse('subContractedDetails', workOrder, []).length > 0;

	const buyerAttachments = nullSafeGetOrElse('buyerAttachments', workOrder, []);
	const supplierAttachments = nullSafeGetOrElse('supplierAttachments', workOrder, []);

	const totalAttachments = buyerAttachments.length + supplierAttachments.length;
	const starNoteDisplay = starNote
		? renderWithHighlight(highlight, 'stars.starNote', starNote, true)
		: undefined;
	const starPill = labelsTrackEnabled ? (
		<StarNotePopover
			workOrderId={workOrder.id}
			initialStarred={starred}
			initialNote={starNote || ''}
			noteDisplay={starNoteDisplay}
		>
			<WorkOrderStarIcon size="md" withDropdown />
		</StarNotePopover>
	) : null;

	return (
		<div className="rowColumn group">
			{moveStarToNewLine && <div className="flex items-center">{starPill}</div>}
			<div
				ref={rowPrimaryRef}
				className="rowPrimary relative flex-wrap !items-center !justify-start !gap-3"
			>
				{!moveStarToNewLine && starPill}
				<PrivateWorkOrderLock
					workOrder={workOrder}
					style={{ marginTop: -6, marginRight: 8, color: 'red' }}
					fontSize={16}
				/>
				<div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
					<div ref={titleSlotRef} className="relative min-w-0 shrink overflow-hidden">
						<Ellipsis
							tooltip={true}
							fullWidthRecognition={true}
							lines={showFullTitle ? undefined : 1}
							style={{
								color: 'rgba(0,0,0,0.65)',
								paddingBottom: '1px',
								overflow: 'hidden',
								lineHeight: 1.5,
								wordBreak: 'break-word',
								whiteSpace: 'normal',
							}}
							className="[&_span]:break-all"
						>
							{renderWithHighlight(highlight, 'title', getSanitisedWorkOrder(workOrder).title)}
							{titleActions && titleActions(workOrder)}
						</Ellipsis>
						<span
							ref={titleMeasureRef}
							aria-hidden="true"
							className="pointer-events-none invisible absolute left-0 top-0 w-full whitespace-nowrap"
						>
							{getSanitisedWorkOrder(workOrder).title || ''}
						</span>
					</div>
					{labelsTrackEnabled && !labelsOnNewLine && (
						<div className="shrink-0">
							<div ref={labelsInnerRef} className="inline-flex flex-wrap items-center gap-3">
								<WorkOrderLabelChipsRow
									labels={workOrderLabels}
									layout="inline"
									maxVisible={workOrderLabels.length}
									showAddButton
									revealAddButtonOnHover
									onAddClick={() => setAddLabelModalOpen(true)}
									loading={labelsPending}
								/>
							</div>
						</div>
					)}
				</div>
				{labelsTrackEnabled && labelsOnNewLine && (
					<div className="basis-full">
						<div ref={labelsInnerRef} className="inline-flex flex-wrap items-center gap-3">
							<WorkOrderLabelChipsRow
								labels={workOrderLabels}
								layout="inline"
								maxVisible={workOrderLabels.length}
								showAddButton
								revealAddButtonOnHover
								onAddClick={() => setAddLabelModalOpen(true)}
								loading={labelsPending}
							/>
						</div>
					</div>
				)}
			</div>
			<div className="rowSecondary">
				<div className="rowInlineGroup">
					<span className="rowGrayChip">
						<i className="icons8-font icons8-location-marker" />
						{renderWithHighlight(
							highlight,
							'locationName',
							nullSafeGet('location.name', getSanitisedWorkOrder(workOrder))
						)}
					</span>
				</div>
				{nullSafeGet('asset.name', workOrder) ? (
					<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
						<span>
							<i className="icons8-font icons8-bulldozer" />
							{renderWithHighlight(
								highlight,
								'assetName',
								getAssetDisplayName(nullSafeGet('asset', workOrder))
							)}
							{nullSafeGet('asset.assetNumber', workOrder) && (
								<span>
									{' '}
									#
									{renderWithHighlight(
										highlight,
										'assetNumber',
										nullSafeGet('asset.assetNumber', workOrder)
									)}
								</span>
							)}
						</span>
					</div>
				) : null}
				{nullSafeGet('isWarranty', workOrder) ? (
					<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
						{warrantyDisplay('Active')}
					</div>
				) : null}
				<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
					<WorkOrderDisplayStatusDisplay status={getSanitisedWorkOrder(workOrder).displayStatus} />
				</div>
				<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
					<span
						style={{
							color: getWorkOrderBasePriorityBackgroundColor(
								getSanitisedWorkOrder(workOrder).woPriority
							),
							backgroundColor: getWorkOrderBasePriorityPillBackgroundColor(
								getSanitisedWorkOrder(workOrder).woPriority
							),
							padding: '4px 12px',
							borderRadius: 16,
						}}
					>
						{renderWithHighlight(
							highlight,
							'woPriorityName',
							nullSafeGet('woPriority.name', getSanitisedWorkOrder(workOrder))
						)}
					</span>
				</div>
				<WorkOrderTechAssignedPill workOrder={workOrder} />
				{workOrder.isCapex ? (
					<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
						<span
							className={'rowGrayChip'}
							style={{
								padding: '4px 12px',
								borderRadius: 16,
							}}
						>
							CapEx
						</span>
					</div>
				) : null}
				<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
					<i className="icons8-font icons8-maintenance-filled" />
					<span>
						{renderWithHighlight(
							highlight,
							'problemTypeHierarchyName',
							nullSafeGetOrElse(
								'problemType.hierarchyName',
								getSanitisedWorkOrder(workOrder),
								nullSafeGet('problemType.name', getSanitisedWorkOrder(workOrder))
							)
						)}
					</span>
				</div>
				{!hideSupplierName && getBuyerOrContractorNameWithHighlightKey(workOrder, currentUser) ? (
					<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
						<i className="icons8-font icons8-company" />
						{renderWithHighlight(
							highlight,
							getBuyerOrContractorNameWithHighlightKey(workOrder, currentUser).key,
							getBuyerOrContractorNameWithHighlightKey(workOrder, currentUser).value,
							true
						)}
					</div>
				) : null}
				<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
					<span>WO #{renderWithHighlight(highlight, 'id', nullSafeGet('id', workOrder))}</span>
				</div>

				{totalAttachments > 0 ? (
					<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
						<Popover
							title="Attachments"
							content={
								<div className="attachmentsPopover">
									{buyerAttachments.length > 0 && (
										<div className="flex flex-row items-center justify-between gap-x-6">
											<div>{userType === ROLE_TYPES.BUYER ? 'Your' : 'Customer'} Attachments</div>
											<span className="text-sm text-gray-500">{buyerAttachments.length}</span>
										</div>
									)}
									{supplierAttachments.length > 0 && (
										<div className="flex flex-row items-center justify-between gap-x-6">
											<div>{userType === ROLE_TYPES.BUYER ? 'Supplier' : 'Your'} Attachments</div>
											<span className="text-sm text-gray-500">{supplierAttachments.length}</span>
										</div>
									)}
								</div>
							}
						>
							<span className="rowGrayChip">
								<i className="icons8-font icons8-attachment" />
								{totalAttachments} Attachment
								{totalAttachments > 1 ? 's' : ''}
							</span>
						</Popover>
					</div>
				) : null}
				{nullSafeGet('subContractedToWorkOrder.subSupplierFacilityId', workOrder) ? (
					<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
						<span className="rowGrayChip">
							Subcontracted:{' '}
							{getStatusDisplayName(
								nullSafeGet('subContractedToWorkOrder.contractorDisplayStatus', workOrder)
							)}
						</span>
					</div>
				) : null}
				{nullSafeGet('poNumber', workOrder) ? (
					<div className="rowInlineGroup" style={{ display: 'inline-flex' }}>
						<span>
							PO #{renderWithHighlight(highlight, 'poNumber', nullSafeGet('poNumber', workOrder))}
						</span>
					</div>
				) : null}
				{nullSafeGet('buyerProjects.length', workOrder) === 1 ? (
					<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
						<span>
							<i className="icons8-font icons8-blueprint" />
							{renderWithHighlight(
								highlight,
								'buyerProjects',
								nullSafeGet('buyerProjects', workOrder)[0].title
							)}
						</span>
					</div>
				) : nullSafeGet('buyerProjects.length', workOrder) > 1 ? (
					<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
						<span>
							<i className="icons8-font icons8-blueprint" />
							{nullSafeGet('buyerProjects.length', workOrder)} Projects
						</span>
					</div>
				) : null}

				{nullSafeGet('linkedToWorkOrders.length', workOrder) ? (
					<div className="rowInlineGroup" style={{ display: simpler ? 'none' : 'inline-flex' }}>
						<span>
							<i className="icons8-font icons8-link" />
							{nullSafeGet('linkedToWorkOrders.length', workOrder)} Work orders
						</span>
					</div>
				) : null}
			</div>
			{labelsTrackEnabled && addLabelModalOpen && (
				<span className="absolute" onClick={(event) => event.stopPropagation()}>
					<AddLabelModal
						visible={addLabelModalOpen}
						onClose={() => setAddLabelModalOpen(false)}
						workOrderId={workOrder.id}
						existingLabels={workOrderLabels}
					/>
				</span>
			)}

			{highlight &&
			((!hideLastNotesForSubcontracted && nullSafeGetOrElse('notes', highlight, []).length > 0) ||
				((isSupplierPayingCustomer(currentUser) || isInternalTech(currentUser)) &&
					isClientView &&
					!hideLastNotesForSubcontracted &&
					nullSafeGetOrElse('internalSpaces', highlight, []).length > 0) ||
				nullSafeGetOrElse('troubleshooting', highlight, []).length > 0 ||
				Object.keys(highlight).some(
					(key) =>
						!displayedFields[key] &&
						highlightLabels[key] &&
						(highlight[key] || []).length > 0 &&
						!(hideLastNotesForSubcontracted && NOTE_HIGHLIGHT_KEYS.includes(key))
				)) ? (
				(() => {
					// In search-highlight mode every note hit is rendered as a uniform simple
					// chip — no avatar, timestamp, or last-note chrome. The chip is driven
					// entirely by the backend `highlight` object so the UI is a faithful echo
					// of what matched the search. Public notes and internal notes are split
					// into two labeled rows so users can tell them apart at a glance, and
					// each row routes to its own tab on click (mirrors WorkOrderRowNotePreview).
					const notesHL = hideLastNotesForSubcontracted
						? []
						: nullSafeGetOrElse('notes', highlight, []);
					// Internal notes are only visible to suppliers viewing in client mode
					// (paying-customer suppliers or internal techs). Buyers must never see
					// internal-space hits, so we drop them from the highlight list here —
					// mirrors the showMultiChannel gate used in WorkOrderRowNotePreview.
					// Subcontracted rows suppress them too (OPE-4842).
					const canViewInternalNotes =
						(isSupplierPayingCustomer(currentUser) || isInternalTech(currentUser)) &&
						isClientView &&
						!hideLastNotesForSubcontracted;
					const internalHL = canViewInternalNotes
						? nullSafeGetOrElse('internalSpaces', highlight, [])
						: [];
					const handleNotesChipClick = (event: React.MouseEvent) => {
						stopDefaultEvents(event);
						history.push(
							getWorkOrderLink({
								userType,
								tab: 'notes',
								workOrder,
								targetCollectionName,
								location,
								BACK_TEXT: 'Back to Work Orders',
							})
						);
					};
					const handleInternalNotesChipClick = (event: React.MouseEvent) => {
						stopDefaultEvents(event);
						openInternalWorkspace('notes');
						history.push(
							getWorkOrderLink({
								userType,
								tab: 'internalSpace',
								workOrder,
								targetCollectionName,
								location,
								BACK_TEXT: 'Back to Work Orders',
							})
						);
					};
					// Match the pill styling used by the "Other highlight sections" iteration
					// below (Description, Check In Notes, etc.) so every search-result chip
					// reads as the same shape — only the label column distinguishes them.
					const renderSimpleNoteChip = (
						html: string,
						key: string | number,
						onClick: (event: React.MouseEvent) => void
					) => (
						<span
							key={key}
							onClick={onClick}
							className="inline-flex h-[28px] min-w-0 max-w-full cursor-pointer items-center rounded-[12px] bg-grey-3 px-3 hover:bg-grey-5"
						>
							<span
								className="highlights footnoteMedium min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-grey-8"
								dangerouslySetInnerHTML={{ __html: html }}
							/>
						</span>
					);
					return (
						<div
							style={{
								display: 'grid',
								gridTemplateColumns: 'auto 1fr',
								columnGap: 12,
								rowGap: 12,
								position: 'relative',
							}}
						>
							{/* Single continuous separator — position tracks the label column width. */}
							<GridColumnSeparator />

							{/* Notes row — one pill per public-note highlight entry. */}
							{notesHL.length > 0 ? (
								<>
									<span className="footnoteregular shrink-0 self-start whitespace-nowrap pr-3 pt-1 text-grey-8">
										Notes:
									</span>
									<div className="flex min-w-0 flex-col items-start gap-2 py-1">
										{notesHL.map((html, idx) =>
											renderSimpleNoteChip(html, idx, handleNotesChipClick)
										)}
									</div>
								</>
							) : null}

							{/* Internal Notes row — kept distinct from public notes so users can
							    tell at a glance which channel a hit came from. */}
							{internalHL.length > 0 ? (
								<>
									<span className="footnoteregular shrink-0 self-start whitespace-nowrap pr-3 pt-1 text-grey-8">
										Internal Notes:
									</span>
									<div className="flex min-w-0 flex-col items-start gap-2 py-1">
										{internalHL.map((html, idx) =>
											renderSimpleNoteChip(html, idx, handleInternalNotesChipClick)
										)}
									</div>
								</>
							) : null}

							{/* Other highlight sections (Check in Notes, Description, etc.) */}
							{Object.keys(highlight).map((key) => {
								if (
									!displayedFields[key] &&
									highlightLabels[key] &&
									!(hideLastNotesForSubcontracted && NOTE_HIGHLIGHT_KEYS.includes(key))
								) {
									const entries = highlight[key] || [];
									if (!entries.length) return null;
									return (
										<React.Fragment key={key}>
											<span className="footnoteregular shrink-0 self-start whitespace-nowrap pr-3 pt-1 text-grey-8">
												{highlightLabels[key]}:
											</span>
											<div className="flex min-w-0 flex-col items-start gap-2 py-1">
												{entries.map((entry, index) => (
													<span
														key={index}
														className="inline-flex h-[28px] min-w-0 max-w-full items-center rounded-[12px] bg-grey-3 px-3"
													>
														<span
															className="highlights footnoteMedium min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-grey-8"
															dangerouslySetInnerHTML={{
																__html: applySearchCasing(entry),
															}}
														/>
													</span>
												))}
											</div>
										</React.Fragment>
									);
								}
								return null;
							})}

							{/* Troubleshooting */}
							{highlight.troubleshooting &&
								highlight.troubleshooting.map((note, index) => (
									<React.Fragment key={`troubleshooting-${index}`}>
										<span className="footnoteregular text-8 shrink-0 self-center whitespace-nowrap pr-3">
											Troubleshooting:
										</span>
										<div className="highlights footnoteregular min-w-0 flex-1 py-1 text-grey-8">
											{nullSafeGet('question', note)
												? extractTextContent(nullSafeGet('question', note))
												: null}
											{nullSafeGet('answer', note) ? (
												<span
													className="ml-1"
													dangerouslySetInnerHTML={{
														__html: nullSafeGet('answer', note),
													}}
												/>
											) : null}
										</div>
									</React.Fragment>
								))}
						</div>
					);
				})()
			) : !hideNotePreview && !hideLastNotesForSubcontracted ? (
				<WorkOrderRowNotePreview
					workOrder={workOrder}
					userType={userType}
					targetCollectionName={targetCollectionName}
					refreshTable={refreshTable}
				/>
			) : null}
			<div className="rowTertiary">
				{isNotClosed ? (
					isDueDateSameAsCompletionDate ? (
						isPastDue ? (
							!hideDueDate ? (
								<span
									style={{
										color: 'rgb(163, 40, 40)',
										backgroundColor: 'rgb(249, 230, 230)',
										padding: '4px 12px',
										borderRadius: 16,
									}}
								>
									Past due{' '}
									<DateTimeHover
										timeZone={getWorkOrderTimeZoneId(workOrder)}
										timestamp={workOrder.dueDate}
										showTime={false}
									/>
								</span>
							) : null
						) : (
							<span>
								Reported{' '}
								<DateTimeHover
									timeZone={getWorkOrderTimeZoneId(workOrder)}
									timestamp={workOrder.createdAt}
								/>
							</span>
						)
					) : isPastEstimatedCompletionDate ? (
						[
							isPastDue ? (
								!hideDueDate ? (
									<span
										style={{
											color: 'rgb(163, 40, 40)',
											backgroundColor: 'rgb(249, 230, 230)',
											padding: '4px 12px',
											borderRadius: 16,
										}}
									>
										Past due{' '}
										<DateTimeHover
											timeZone={getWorkOrderTimeZoneId(workOrder)}
											timestamp={workOrder.dueDate}
											showTime={false}
										/>
									</span>
								) : null
							) : (
								<span>
									Reported{' '}
									<DateTimeHover
										timeZone={getWorkOrderTimeZoneId(workOrder)}
										timestamp={workOrder.createdAt}
									/>
								</span>
							),
							<span
								style={{
									color: 'rgb(163, 40, 40)',
									backgroundColor: 'rgb(249, 230, 230)',
									padding: '4px 12px',
									borderRadius: 16,
								}}
							>
								Estimated completion{' '}
								<DateTimeHover
									timeZone={getWorkOrderTimeZoneId(workOrder)}
									timestamp={nullSafeGet('estimatedCompletionDate', workOrder)}
									showDate
								/>{' '}
							</span>,
						]
					) : (
						[
							<span>
								Reported{' '}
								<DateTimeHover
									timeZone={getWorkOrderTimeZoneId(workOrder)}
									timestamp={workOrder.createdAt}
								/>{' '}
							</span>,
							<span>
								Estimated completion{' '}
								<DateTimeHover
									timeZone={getWorkOrderTimeZoneId(workOrder)}
									timestamp={workOrder.estimatedCompletionDate}
								/>
							</span>,
						]
					)
				) : (
					<span>
						Reported{' '}
						<DateTimeHover
							timeZone={getWorkOrderTimeZoneId(workOrder)}
							timestamp={workOrder.createdAt}
						/>
					</span>
				)}

				{workOrder.displayStatus === WORK_ORDER_DISPLAY_STATUSES.inProgress &&
				nullSafeGet('lastServiceCall.checkInTime', workOrder) &&
				!nullSafeGet('lastServiceCall.checkOutTime', workOrder) ? (
					<span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
						<span>
							Checked in at{' '}
							<DateTimeHover
								timeZone={getWorkOrderTimeZoneId(workOrder)}
								timestamp={nullSafeGet('lastServiceCall.checkInTime', workOrder)}
								showDate
							/>
						</span>
					</span>
				) : [
						WORK_ORDER_STATUSES.techScheduled,
						WORK_ORDER_STATUSES.techEnRoute,
						WORK_ORDER_STATUSES.techRescheduled,
						WORK_ORDER_STATUSES.techWorkingOnSite,
				  ].includes(workOrder.status) &&
				  nullSafeGet('lastServiceCall.serviceScheduledAt', workOrder) ? (
					<span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
						<span>
							Service scheduled{' '}
							<DateTimeHover
								timeZone={getWorkOrderTimeZoneId(workOrder)}
								timestamp={nullSafeGet('lastServiceCall.serviceScheduledAt', workOrder)}
								showDate
							/>
						</span>
					</span>
				) : null}

				{workOrder.displayStatus === WORK_ORDER_DISPLAY_STATUSES.complete &&
				nullSafeGet('completedAt', workOrder) ? (
					<span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
						<span>
							Completed at{' '}
							<DateTimeHover
								timeZone={getWorkOrderTimeZoneId(workOrder)}
								timestamp={nullSafeGet('completedAt', workOrder)}
								showTime={false}
							/>
						</span>
					</span>
				) : null}
			</div>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	isClientView: state.clientView.isClientView,
	companyConfig: state.company_config.detail,
	labelsPending: nullSafeGetOrElse(
		`work_order_star_labels.labelsPendingById.${nullSafeGet('workOrder.id', ownProps)}`,
		state,
		false
	),
});

const mapDispatchToProps = {
	openInternalWorkspace,
};

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(WorkOrderRowDisplay));
